import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const PRESETS = [
  { id: '7d', label: 'Last 7 days' },
  { id: '14d', label: 'Last 14 days' },
  { id: 'month', label: 'This month' },
  { id: 'custom', label: 'Custom' },
];

const fmt = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
};

function healthyNow(count, band) {
  const low = Number(band?.low);
  const high = Number(band?.high);
  if (!Number.isFinite(low) || !Number.isFinite(high)) return true;
  return count >= low && count <= high;
}

const OverallHealthTab = () => {
  const { isDarkMode } = useTheme();
  const [preset, setPreset] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [thresholds, setThresholds] = useState(null);
  const [viewMode, setViewMode] = useState('active');
  const [picked, setPicked] = useState([]);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [notice, setNotice] = useState('');
  const [periods, setPeriods] = useState([
    { start: '00:00', end: '12:00' },
    { start: '12:00', end: '00:00' },
  ]);
  const [twoPeriods, setTwoPeriods] = useState(true);
  const periodsRef = useRef(periods);
  const twoRef = useRef(twoPeriods);
  periodsRef.current = periods;
  twoRef.current = twoPeriods;

  const load = useCallback(async (next) => {
    const params = { preset: next.preset };
    if (next.preset === 'custom') {
      if (!next.from || !next.to) return;
      params.from = next.from;
      params.to = next.to;
    }
    const chosen = next.periods || periodsRef.current;
    const useTwo = next.twoPeriods ?? twoRef.current;
    const active = useTwo ? chosen : [chosen[0]];
    params.windows = active.map((period) => `${period.start}-${period.end}`).join(',');
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/overall-health', { params });
      const data = res.data;
      setReport(data);
      if (!next.keepThresholds) setThresholds(data.thresholds);
      setViewMode(data.view?.viewMode === 'custom' ? 'custom' : 'active');
      setPicked(data.view?.viewMode === 'custom' ? data.view.modelIds || [] : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not load overall health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ preset: '7d' });
  }, [load]);

  const metrics = report?.metrics || [];

  const models = useMemo(() => {
    if (!report || !thresholds) return [];
    return (report.rows || []).map((row) => ({
      ...row,
      days: (row.days || []).map((day) => ({
        ...day,
        halves: (day.halves || []).map((half) => {
          const cells = {};
          let ok = true;
          for (const metric of metrics) {
            const value = half.metrics?.[metric.key];
            const healthy = healthyNow(value?.total, thresholds[metric.key]);
            cells[metric.key] = { ...value, healthy };
            if (!healthy) ok = false;
          }
          return { ...half, cells, healthy: ok };
        }),
      })),
    }));
  }, [report, thresholds, metrics]);

  const changePreset = (id) => {
    setPreset(id);
    setNotice('');
    if (id !== 'custom') load({ preset: id });
  };

  const applyCustom = () => {
    setNotice('');
    load({ preset: 'custom', from: customFrom, to: customTo });
  };

  const setPeriod = (index, side, value) => {
    const clock = String(value || '').slice(0, 5);
    setPeriods((prev) => prev.map((period, i) => (i === index ? { ...period, [side]: clock } : period)));
  };

  const setBand = (key, side, raw) => {
    const value = raw === '' ? '' : Number(raw);
    setThresholds((prev) => ({
      ...prev,
      [key]: { ...prev[key], [side]: value },
    }));
  };

  const saveThresholds = async () => {
    setSavingThresholds(true);
    setNotice('');
    setError('');
    try {
      await api.put('/admin/overall-health/thresholds', { thresholds });
      setNotice('Average low and high saved.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  const toggleModel = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveView = async () => {
    setSavingView(true);
    setNotice('');
    setError('');
    try {
      await api.put('/admin/overall-health/view', {
        viewMode,
        modelIds: viewMode === 'custom' ? picked : [],
      });
      setNotice(viewMode === 'active' ? 'Default view saved: active models.' : 'Default view saved.');
      await load({ preset, from: customFrom, to: customTo, keepThresholds: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save the view');
    } finally {
      setSavingView(false);
    }
  };

  const card = isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const text = isDarkMode ? 'text-white' : 'text-slate-900';

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <div>
              <h2 className={`text-base font-semibold ${text}`}>Overall health</h2>
              <p className={`text-xs ${muted}`}>
                {report
                  ? `${report.range.from} → ${report.range.to} · ${report.range.days} days · AMS`
                  : 'Last 7 days · AMS'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => load({ preset, from: customFrom, to: customTo })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
              isDarkMode ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className={muted}>Periods</span>
          {[0, 1].filter((index) => index === 0 || twoPeriods).map((index) => (
            <span key={index} className="inline-flex items-center gap-1">
              {index === 1 && <span className={muted}>and</span>}
              <input
                type="time"
                value={periods[index].start}
                onChange={(e) => setPeriod(index, 'start', e.target.value)}
                className={`px-2 py-1 text-sm rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                aria-label={`Period ${index + 1} start`}
              />
              <span className={muted}>–</span>
              <input
                type="time"
                value={periods[index].end}
                onChange={(e) => setPeriod(index, 'end', e.target.value)}
                className={`px-2 py-1 text-sm rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                aria-label={`Period ${index + 1} end`}
              />
            </span>
          ))}
          <button
            type="button"
            onClick={() => load({ preset, from: customFrom, to: customTo })}
            className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !twoPeriods;
              setTwoPeriods(next);
              load({ preset, from: customFrom, to: customTo, twoPeriods: next });
            }}
            className={`px-3 py-1.5 text-sm rounded-lg ${isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}
          >
            {twoPeriods ? 'One period' : 'Two periods'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {PRESETS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => changePreset(item.id)}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                preset === item.id
                  ? 'bg-emerald-600 text-white'
                  : isDarkMode
                    ? 'bg-slate-700 text-slate-200'
                    : 'bg-slate-100 text-slate-700'
              }`}
            >
              {item.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className={`px-2 py-1.5 text-sm rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
              />
              <span className={muted}>to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className={`px-2 py-1.5 text-sm rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
              />
              <button
                type="button"
                onClick={applyCustom}
                className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900"
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`text-sm font-medium ${text}`}>Models</p>
            <p className={`text-xs ${muted}`}>Saved separately from the average bands. Default is active models only.</p>
          </div>
          <button
            type="button"
            onClick={saveView}
            disabled={savingView}
            className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white disabled:opacity-60"
          >
            {savingView ? 'Saving…' : 'Save default view'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <label className={`inline-flex items-center gap-2 ${text}`}>
            <input
              type="radio"
              checked={viewMode === 'active'}
              onChange={() => setViewMode('active')}
            />
            Active models only
          </label>
          <label className={`inline-flex items-center gap-2 ${text}`}>
            <input
              type="radio"
              checked={viewMode === 'custom'}
              onChange={() => {
                setViewMode('custom');
                if (picked.length === 0) {
                  setPicked((report?.catalog || []).filter((m) => m.isActive).map((m) => m.id));
                }
              }}
            />
            Choose models
          </label>
        </div>
        {viewMode === 'custom' && (
          <div className={`mt-3 max-h-40 overflow-auto rounded-lg border p-2 grid grid-cols-2 md:grid-cols-4 gap-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            {(report?.catalog || []).map((model) => (
              <label key={model.id} className={`flex items-center gap-2 text-sm px-1 py-0.5 ${text}`}>
                <input
                  type="checkbox"
                  checked={picked.includes(model.id)}
                  onChange={() => toggleModel(model.id)}
                />
                <span className="truncate">{model.name}</span>
                {!model.isActive && <span className={`text-xs ${muted}`}>inactive</span>}
              </label>
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {notice && <p className={`text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{notice}</p>}

      {thresholds && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={`text-sm font-medium ${text}`}>Average low / high</p>
              <p className={`text-xs ${muted}`}>Applied to each half-day. Saved separately from the model view.</p>
            </div>
            <button
              type="button"
              onClick={saveThresholds}
              disabled={savingThresholds}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white disabled:opacity-60"
            >
              {savingThresholds ? 'Saving…' : 'Save averages'}
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {metrics.map((metric) => (
              <div key={metric.key}>
                <p className={`text-xs mb-1 ${muted}`}>{metric.label}</p>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    value={thresholds[metric.key]?.low ?? ''}
                    onChange={(e) => setBand(metric.key, 'low', e.target.value)}
                    className={`w-full px-1.5 py-1 text-xs rounded border tabular-nums ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                    aria-label={`${metric.label} average low`}
                  />
                  <input
                    type="number"
                    min="0"
                    value={thresholds[metric.key]?.high ?? ''}
                    onChange={(e) => setBand(metric.key, 'high', e.target.value)}
                    className={`w-full px-1.5 py-1 text-xs rounded border tabular-nums ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                    aria-label={`${metric.label} average high`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <p className={`text-sm ${muted}`}>Loading…</p>}

      {!loading && models.length === 0 && (
        <div className={`rounded-xl border px-4 py-8 text-center text-sm ${card} ${muted}`}>
          No models in this view.
        </div>
      )}

      {models.map((model) => (
        <div key={model.modelId} className={`rounded-xl border overflow-hidden ${card}`}>
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <p className={`text-sm font-semibold ${text}`}>{model.name}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                  <th className={`text-left font-medium px-4 py-2 sticky left-0 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>Day</th>
                  {metrics.map((metric) => (
                    <th key={metric.key} className="text-right font-medium px-3 py-2 whitespace-nowrap">{metric.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.days.map((day) => day.halves.map((half, index) => {
                  const open = half.inProgress;
                  const night = !open && index === 1;
                  const toneRow = open
                    ? isDarkMode ? 'bg-orange-950/70' : 'bg-orange-50'
                    : night
                      ? isDarkMode ? 'bg-slate-900/40' : 'bg-slate-50'
                      : '';
                  const stickyBg = open
                    ? isDarkMode ? 'bg-orange-950' : 'bg-orange-50'
                    : night
                      ? isDarkMode ? 'bg-slate-900' : 'bg-slate-50'
                      : isDarkMode ? 'bg-slate-800' : 'bg-white';
                  return (
                    <tr key={`${day.date}-${half.key}`} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'} ${toneRow}`}>
                      <td className={`px-4 py-2.5 sticky left-0 ${stickyBg}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${open ? 'bg-orange-400' : half.healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          <span className={`font-medium ${text}`}>{index === 0 ? day.label : ''}</span>
                          <span className={`text-xs ${open ? (isDarkMode ? 'text-orange-300' : 'text-orange-700') : muted}`}>{half.label}</span>
                          {open && <span className={`text-xs font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>in progress</span>}
                        </div>
                      </td>
                      {metrics.map((metric) => {
                        const cell = half.cells[metric.key];
                        const tone = cell?.healthy
                          ? isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
                          : isDarkMode ? 'text-red-300' : 'text-red-700';
                        return (
                          <td key={metric.key} className={`px-3 py-2.5 text-right font-semibold tabular-nums ${tone}`}>
                            {fmt(cell?.total)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OverallHealthTab;
